import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft } from '@phosphor-icons/react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ProposalsPage = () => {
  const iframeRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const role = user?.role || 'staff';
  const [search] = useSearchParams();
  const startId = search.get('id') || '';
  const [key, setKey] = useState(Date.now());

  useEffect(() => { setKey(Date.now()); }, [location.key]);

  const sendToIframe = useCallback((msg) => {
    const win = iframeRef.current?.contentWindow;
    if (win) win.postMessage(msg, '*');
  }, []);

  const fetchClients = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/clients`, { withCredentials: true });
      sendToIframe({ type: 'PROPOSAL_CLIENTS_DATA', clients: res.data || [], role });
    } catch (e) {
      sendToIframe({ type: 'PROPOSAL_CLIENTS_ERROR', error: e.message });
    }
  }, [role, sendToIframe]);

  const fetchProposals = useCallback(async (deepLinkId) => {
    try {
      const res = await axios.get(`${API_URL}/api/proposals`, { withCredentials: true });
      sendToIframe({ type: 'PROPOSAL_LIST_DATA', proposals: res.data || [], startProposalId: deepLinkId || startId });
    } catch (_) {
      sendToIframe({ type: 'PROPOSAL_LIST_DATA', proposals: [], startProposalId: deepLinkId || startId });
    }
  }, [startId, sendToIframe]);

  const onLoad = useCallback(() => {
    // Send both clients and proposals on iframe load
    fetchClients();
    fetchProposals();
  }, [fetchClients, fetchProposals]);

  useEffect(() => {
    const onMsg = (e) => {
      const d = e.data || {};
      if (d.type === 'PROPOSAL_REQUEST_CLIENTS') fetchClients();
      if (d.type === 'PROPOSAL_REQUEST_LIST') fetchProposals(d.startProposalId);

      if (d.type === 'PROPOSAL_SAVE_NOW') {
        const p = d.proposal;
        if (!p || !p.id) return;
        const put = axios.put(`${API_URL}/api/proposals/${encodeURIComponent(p.id)}`, p, { withCredentials: true });
        put.then(() => sendToIframe({ type: 'PROPOSAL_SAVE_OK', proposalId: p.id }))
          .catch((err) => {
            if (err.response?.status === 404) {
              axios.post(`${API_URL}/api/proposals`, p, { withCredentials: true })
                .then(() => sendToIframe({ type: 'PROPOSAL_SAVE_OK', proposalId: p.id }))
                .catch((e2) => sendToIframe({ type: 'PROPOSAL_SAVE_ERR', proposalId: p.id, error: e2.response?.data?.detail || e2.message }));
            } else {
              sendToIframe({ type: 'PROPOSAL_SAVE_ERR', proposalId: p.id, error: err.response?.data?.detail || err.message });
            }
          });
      }
      if (d.type === 'PROPOSAL_DELETE') {
        axios.delete(`${API_URL}/api/proposals/${encodeURIComponent(d.proposalId)}`, { withCredentials: true }).catch(() => {});
      }
      if (d.type === 'PROPOSAL_SUBMIT_FOR_REVIEW') {
        const pid = d.proposalId;
        const fullProposal = d.proposal || {};
        axios.post(`${API_URL}/api/proposals/${encodeURIComponent(pid)}/submit-for-review`, fullProposal, { withCredentials: true })
          .then(() => sendToIframe({ type: 'PROPOSAL_SUBMIT_OK', proposalId: pid }))
          .catch((err) => sendToIframe({ type: 'PROPOSAL_SUBMIT_ERR', proposalId: pid, error: err.response?.data?.detail || err.message }));
      }
      if (d.type === 'PROPOSAL_SAVE_CLIENT') {
        const { client, isNew } = d;
        const url = `${API_URL}/api/clients${isNew ? '' : `/${encodeURIComponent(client.id)}`}`;
        const method = isNew ? 'post' : 'put';
        axios[method](url, client, { withCredentials: true }).then(fetchClients).catch(() => {});
      }
      if (d.type === 'PROPOSAL_DELETE_CLIENT') {
        axios.delete(`${API_URL}/api/clients/${encodeURIComponent(d.id)}`, { withCredentials: true })
          .then(fetchClients).catch(() => {});
      }
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, [fetchClients, fetchProposals, sendToIframe]);

  return (
    <div data-testid="proposals-page" style={{ margin: '-24px', display: 'flex', flexDirection: 'column', height: 'calc(100vh - 80px)' }}>
      <div className="px-4 py-2 flex items-center gap-2 border-b" style={{ background: '#fff' }}>
        <Button variant="outline" size="sm" onClick={() => navigate('/proposals')} data-testid="back-to-pipeline-btn">
          <ArrowLeft size={14} className="mr-1" /> {role === 'admin' ? 'Back to Pipeline' : 'Back to Overview'}
        </Button>
      </div>
      <iframe
        ref={iframeRef}
        key={key}
        src={`${API_URL}/api/proposal-builder?v=${key}${startId ? `&id=${encodeURIComponent(startId)}` : ''}`}
        title="Proposal Builder"
        data-testid="proposal-builder-iframe"
        onLoad={onLoad}
        style={{ width: '100%', flex: 1, border: 0, display: 'block', background: '#fff' }}
      />
    </div>
  );
};

export default ProposalsPage;
